import { useCustom } from '@refinedev/core';
import { Spin } from 'antd';
import { Users, UserCheck, Coins, Pentagon } from 'lucide-react';
import { useEffect, type FC } from 'react';
import { GuidedTour } from '@/components/onboarding/GuidedTour';
import { usePageTour } from '@/hooks/usePageTour';
import { referralDashboardTourSteps } from '@/lib/tour-data';

import StatCard from '@/components/common/StatCard';
import TopSponsorsTable from './components/TopSponsorsTable';
import NetworkStatsCards from './components/NetworkStatsCards';
import DividendRatesCard from './components/DividendRatesCard';

interface NetworkStats {
  totalSponsors: number;
  totalReferrals: number;
  totalValidated: number;
  totalPending: number;
  totalTokensDistributed: number;
  averageNetworkSize: number;
  completedPentagons: number;
}

interface TopSponsor {
  sponsorId: string;
  totalTokens: number;
  validatedReferrals: number;
  pentagonLevel: number;
}

const TOUR_ID = 'admin-referral-dashboard';

const ReferralDashboardPage: FC = () => {
  const tour = usePageTour(TOUR_ID);
  useEffect(() => {
    const handler = () => tour.startTour();
    window.addEventListener('restart-page-tour', handler);
    return () => window.removeEventListener('restart-page-tour', handler);
  }, [tour]);

  const {
    data: statsData,
    isLoading: statsLoading,
    isError: statsError,
  } = useCustom<{ data: NetworkStats }>({
    url: '/api/v1/admin/referral/network-stats',
    method: 'get',
  });

  const {
    data: sponsorsData,
    isLoading: sponsorsLoading,
  } = useCustom<{ data: TopSponsor[] }>({
    url: '/api/v1/admin/referral/top-sponsors',
    method: 'get',
    config: { query: { limit: 10 } },
  });

  const stats = statsData?.data?.data;
  const sponsors = sponsorsData?.data?.data;

  if (statsLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (statsError) {
    return (
      <div className="space-y-6">
        <GradientHeader />
        <div className="fc-card border-red-300 p-6 dark:border-red-800">
          <p className="font-medium text-red-600 dark:text-red-400">
            Impossible de charger les statistiques du réseau de parrainage.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <GradientHeader />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Sponsors"
          value={stats?.totalSponsors}
          icon={Users}
          iconColor="text-blue-600 dark:text-blue-400"
          iconBg="bg-blue-50 dark:bg-blue-900/30"
        />
        <StatCard
          title="Filleuls Validés"
          value={stats?.totalValidated}
          icon={UserCheck}
          iconColor="text-emerald-600 dark:text-emerald-400"
          iconBg="bg-emerald-50 dark:bg-emerald-900/30"
        />
        <StatCard
          title="Tokens Distribués"
          value={stats?.totalTokensDistributed}
          icon={Coins}
          iconColor="text-amber-600 dark:text-amber-400"
          iconBg="bg-amber-50 dark:bg-amber-900/30"
        />
        <StatCard
          title="Pentagones Complétés"
          value={stats?.completedPentagons}
          icon={Pentagon}
          iconColor="text-violet-600 dark:text-violet-400"
          iconBg="bg-violet-50 dark:bg-violet-900/30"
        />
      </div>

      {/* Top Sponsors */}
      <TopSponsorsTable sponsors={sponsors} loading={sponsorsLoading} />

      {/* Network detail + Dividend rates */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <NetworkStatsCards
          totalReferrals={stats?.totalReferrals}
          totalValidated={stats?.totalValidated}
          totalPending={stats?.totalPending}
          averageNetworkSize={stats?.averageNetworkSize}
        />
        <DividendRatesCard />
      </div>
      <GuidedTour tourId={TOUR_ID} steps={referralDashboardTourSteps} isActive={tour.isActive} onComplete={tour.completeTour} onSkip={tour.skipTour} />
    </div>
  );
};

const GradientHeader: FC = () => (
  <div className="fc-gradient-header rounded-2xl p-6 text-white">
    <div className="flex items-center gap-3" data-tour="referral-dashboard-header">
      <Users className="h-6 w-6" />
      <div>
        <h1 className="text-xl font-bold">Referral Network</h1>
        <p className="mt-0.5 text-xs italic text-blue-200">Reseau de parrainage</p>
        <p className="mt-0.5 text-sm text-blue-100">
          Pilotage des ambassadeurs, top sponsors et stats du reseau de parrainage
        </p>
      </div>
    </div>
  </div>
);

export default ReferralDashboardPage;
